import { useCallback, useEffect, useMemo, useState } from 'react';

import { OptionType } from '@evinced-private/ui-common';

import { ConfiguredValues } from 'src/common/helpers/ConfigurationWarningsHelper';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import {
	applyWarningsToValueOptions,
	extractAvailableLabelTypes,
	isCoreLabel,
	TestRowSharedType,
	transformLabelValuesToOptions
} from '../helpers';

interface LabelAddition {
	labelType: OptionType;
	value: OptionType;
}

interface UseLabelsManagementProps<T extends TestRowSharedType> {
	testRows: T[];
	tenantConfiguredLabels: ConfigurationLabel[];
	configuredValues: ConfiguredValues;
}

interface UseLabelsManagementReturn {
	selectedLabelType: OptionType[];
	setSelectedLabelType: (labelType: OptionType[]) => void;
	selectedValue: OptionType[];
	setSelectedValue: (value: OptionType[]) => void;
	additions: LabelAddition[];
	labelTypeOptions: OptionType[];
	valueOptions: OptionType[];
	removeAddition: (index: number) => void;
	resetAdditions: () => void;
}
export const useLabelsManagement = <T extends TestRowSharedType>({
	testRows,
	tenantConfiguredLabels,
	configuredValues
}: UseLabelsManagementProps<T>): UseLabelsManagementReturn => {
	const [selectedLabelType, setSelectedLabelType] = useState<OptionType[]>([]);
	const [selectedValue, setSelectedValue] = useState<OptionType[]>([]);
	const [additions, setAdditions] = useState<LabelAddition[]>([]);

	// Extract available label types (excluding those already on tests)
	const labelTypeOptions = useMemo((): OptionType[] => {
		return extractAvailableLabelTypes<T>(testRows, tenantConfiguredLabels);
	}, [testRows, tenantConfiguredLabels]);

	// Transform values for selected label type
	// For core labels, exclude values that are already added
	const valueOptions = useMemo((): OptionType[] => {
		const selectedLabelTypeValue =
			selectedLabelType.length > 0 ? (selectedLabelType[0].value as string) : undefined;
		const isSelectedLabelCore = selectedLabelTypeValue
			? isCoreLabel(selectedLabelTypeValue)
			: false;

		// For core labels, exclude values that are already in additions
		const excludeValues = isSelectedLabelCore
			? additions
					.filter((addition) => addition.labelType.value === selectedLabelTypeValue)
					.map((addition) => addition.value.value as string)
			: undefined;

		const options = transformLabelValuesToOptions(
			tenantConfiguredLabels,
			selectedLabelTypeValue,
			excludeValues
		);

		return applyWarningsToValueOptions(options, selectedLabelTypeValue, configuredValues);
	}, [tenantConfiguredLabels, selectedLabelType, additions, configuredValues]);

	// Auto-add label when both selects have values
	useEffect(() => {
		if (selectedLabelType.length > 0 && selectedValue.length > 0) {
			const newAddition: LabelAddition = {
				labelType: selectedLabelType[0],
				value: selectedValue[0]
			};
			const labelKey = newAddition.labelType.value as string;
			const isCore = isCoreLabel(labelKey);

			setAdditions((prevAdditions) => {
				if (isCore) {
					// For core labels: replace if label type already exists
					const existingIndex = prevAdditions.findIndex(
						(addition) => addition.labelType.value === newAddition.labelType.value
					);
					if (existingIndex >= 0) {
						// Replace existing core label value
						const updated = [...prevAdditions];
						updated[existingIndex] = newAddition;
						return updated;
					}
					// Add new core label
					return [...prevAdditions, newAddition];
				}
				const newLabelTypeValue = String(newAddition.labelType.value).toLowerCase();
				const newValueValue = String(newAddition.value.value).toLowerCase();

				const existingIndex = prevAdditions.findIndex((addition) => {
					const existingLabelTypeValue = String(addition.labelType.value).toLowerCase();
					const existingValueValue = String(addition.value.value).toLowerCase();
					return (
						existingLabelTypeValue === newLabelTypeValue && existingValueValue === newValueValue
					);
				});
				if (existingIndex >= 0) {
					return [...prevAdditions];
				}
				return [...prevAdditions, newAddition];
			});
			// Reset selections for next addition
			setSelectedLabelType([]);
			setSelectedValue([]);
		}
	}, [selectedLabelType, selectedValue]);

	const removeAddition = useCallback((index: number): void => {
		setAdditions((prev) => prev.filter((_, i) => i !== index));
	}, []);

	const resetAdditions = useCallback((): void => {
		setAdditions([]);
		setSelectedLabelType([]);
		setSelectedValue([]);
	}, []);

	const returnValue = useMemo(() => {
		let resSelectedLabelType = selectedLabelType;
		let resSelectedValue = selectedValue;
		if (selectedLabelType.length > 0 && selectedValue.length > 0) {
			resSelectedLabelType = [];
			resSelectedValue = [];
		}

		return {
			selectedLabelType: resSelectedLabelType,
			setSelectedLabelType,
			selectedValue: resSelectedValue,
			setSelectedValue,
			additions,
			labelTypeOptions,
			valueOptions,
			removeAddition,
			resetAdditions
		};
	}, [
		selectedLabelType,
		selectedValue,
		additions,
		labelTypeOptions,
		valueOptions,
		removeAddition,
		resetAdditions
	]);

	return returnValue;
};
